import { useCallback, useRef, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import {
  CheckIcon,
  ChildrenIcon,
  Eyebrow,
  InfoIcon,
  PlayIcon,
  StatusPill,
} from '../../../shared/components'
import { ChevronIcon } from '../../../shared/ui/icons'
import { GAME_ART_BY_ID, GAME_FOR_SKILL, type GameArt } from '../../../shared/ui/gameArt'
import type { GameId } from '../../../shared/curriculum/types'
import { isContentSkill, type Skill } from '../../../shared/types/prisma-enums'
import type { SchoolSkillRow } from '../../../shared/api/school'
import { ADMIN_COPY } from './adminCopy'
import type { Lang } from './copy'

/**
 * The school tier's playful surface.
 *
 * 🔴 THE SAME JUSTIFICATION AS THE PARENT TIER, AND IT HAD TO BE EARNED AGAIN.
 * design-system.md is explicit that an adult shell's warmth comes *"never from
 * cartoon elements"*, and a head teacher's console is further from a child than
 * a parent's phone is — a mascot dropped in here to look friendly would be
 * exactly what that rule forbids.
 *
 * So the artwork on this screen is the artwork of the **product the school
 * bought**: the five game plates, each carrying that game's own numbers. A head
 * teacher who cannot read a skill table can still see that four of the five
 * games are being used and Kalqy World is not — which is a question they can
 * take to a teacher on Monday.
 *
 * ⚠️ THE ONE PIECE OF PURE DECORATION IN THIS TIER IS THE VILLAGE HOUSE, AND IT
 * IS NOT ON THIS SCREEN — it is the sidebar's, so it appears on all six school
 * screens rather than twice on this one. `AppRouter` records why a building was
 * the right choice where a character was not.
 *
 * 🔴 AND IT DRAWS DATA THE SERVER WAS ALREADY SENDING TO NOBODY.
 * `SchoolOverview.skills` — per-skill events and children, folded across the
 * whole school by `foldSchoolSkills` — was fetched on every load of this screen
 * and rendered **nowhere**. The only thing reading it was
 * `childrenTouchingSkills`, which flattens five rows into one number. That is
 * §8.4's own warning in miniature: *"the predecessor rendered 8 hardcoded
 * children while working endpoints sat uncalled"*.
 *
 * ⚠️ NO PERCENTAGE, ANYWHERE, for the reason `SchoolOverview` states in its own
 * docblock: *"12 of 40 children played this week" is a fact a head teacher can
 * act on; "30% engagement" is a grade whose meaning depends entirely on what it
 * was divided by."* Every number below arrives with the denominator it belongs
 * to, in the same sentence.
 */

/* ────────────────────────────────────────────── the pointer, as two numbers ── */

/**
 * ⚠️ THE SECOND COPY OF THIS HOOK, AND THE DUPLICATION IS DELIBERATE FOR NOW.
 * The parent tier has the same twelve lines. Hoisting it into `shared/` would
 * make a hook that writes CSS custom properties a shared API before a third
 * caller exists to shape it — and both copies are presentation-only, write two
 * variables and read nothing back. 📋 Worth merging when a third tier wants it.
 */
function usePointerVars(): {
  ref: React.RefObject<HTMLDivElement | null>
  onMouseMove: (e: MouseEvent<HTMLElement>) => void
  onMouseLeave: () => void
} {
  const ref = useRef<HTMLDivElement | null>(null)

  const onMouseMove = useCallback((e: MouseEvent<HTMLElement>) => {
    const node = ref.current
    if (!node) return
    const box = node.getBoundingClientRect()
    if (box.width === 0 || box.height === 0) return
    const x = (e.clientX - box.left) / box.width
    const y = (e.clientY - box.top) / box.height
    node.style.setProperty('--mx', `${String(Math.round(x * 100))}%`)
    node.style.setProperty('--my', `${String(Math.round(y * 100))}%`)
    node.style.setProperty('--tx', (x * 2 - 1).toFixed(3))
    node.style.setProperty('--ty', (y * 2 - 1).toFixed(3))
  }, [])

  const onMouseLeave = useCallback(() => {
    const node = ref.current
    if (!node) return
    node.style.setProperty('--tx', '0')
    node.style.setProperty('--ty', '0')
  }, [])

  return { ref, onMouseMove, onMouseLeave }
}

/* ───────────────────────────────────────────────────────────── the hero ── */

export interface SchoolHeroProps {
  lang: Lang
  /** The `<h1>` — the school's own name. */
  schoolName: string
  academicYearLabel: string | null
  /** Children who played inside the window, and the school's total. */
  played: { count: number; total: number }
  /** Children with any recorded activity ever, and the same total. */
  everPlayed: { count: number; total: number }
  windowDays: number
}

/**
 * E1's header.
 *
 * 🔴 THE SCHOOL'S NAME IS THE `<h1>`, WHERE IT USED TO BE HALF OF A SUBTITLE.
 * "School dashboard" was the heading — the same words the sidebar highlights —
 * and the school's own name sat below it in 13px next to the academic year,
 * joined by a middle dot. An operator who has just impersonated into a school is
 * the one reader for whom "which school is this?" is not rhetorical, and that
 * question was being answered in the smallest type on the page.
 *
 * ✅ AND THE TWO DENOMINATOR SENTENCES MOVED INTO IT. They were a `Note` block
 * below the stat grid, which put the most-read fact on the screen — *"1 of 31
 * children played this week"* — underneath five tiles it summarises.
 */
export function SchoolHero({
  lang,
  schoolName,
  academicYearLabel,
  played,
  everPlayed,
  windowDays,
}: SchoolHeroProps) {
  const t = ADMIN_COPY[lang]
  const pointer = usePointerVars()

  return (
    <div
      ref={pointer.ref}
      onMouseMove={pointer.onMouseMove}
      onMouseLeave={pointer.onMouseLeave}
      className="play-rise relative mb-6 overflow-hidden rounded-xl border border-line bg-surface shadow-card"
    >
      {/* One flat tint, for the parent hero's stated reason: every chip on this
          band is `bg-surface/80`, and a gradient would give each of them a
          different contrast depending on where it landed. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: 'var(--color-primary-tint)' }}
      />

      <div className="play-layer relative flex items-center gap-5 px-6 py-5">
        <div className="min-w-0 flex-1">
          <Eyebrow>{t.dashboardTitle}</Eyebrow>
          {/*
            ⚠️ `font-kid` ON THE SCHOOL'S NAME, AND NOWHERE ELSE ON THE SCREEN.
            The same single exception the parent tier makes for a child's name:
            §4's split holds for every other character here, and most of all for
            the numbers, which stay Nunito and tabular.
          */}
          <h1 className="font-kid text-dash-2xl leading-none font-extrabold text-ink">
            {schoolName}
          </h1>
          <p className="mt-1.5 text-dash-sm text-ink-soft">
            {t.academicYear}: {academicYearLabel ?? t.academicYearUnset}
          </p>

          {/*
            🔴 BOTH NUMBERS CARRY THEIR DENOMINATOR INSIDE THE CHIP. OD-39/OD-72:
            the server refuses to send a ratio and the screen must not compute
            one — "1 of 31" is actionable, "3%" is a grade.
          */}
          <ul className="mt-4 flex flex-wrap items-center gap-2">
            <li>
              <Chip
                tint="var(--color-leaf)"
                label={t.playedWindow(windowDays)}
                value={`${String(played.count)} / ${String(played.total)}`}
              >
                <PlayIcon size={15} />
              </Chip>
            </li>
            <li>
              <Chip
                tint="var(--color-sky)"
                label={t.everPlayedLabel}
                value={`${String(everPlayed.count)} / ${String(everPlayed.total)}`}
              >
                <ChildrenIcon size={15} />
              </Chip>
            </li>
          </ul>
        </div>

        {/*
          ✅ THE HOUSE MOVED TO THE SIDEBAR, AND IT BELONGED THERE ALL ALONG.

          It sat here, which meant the one piece of artwork saying "this is your
          school" appeared on the dashboard and on none of the other five school
          screens. In the sidebar it is chrome: present on Reports, Teachers, Our
          plan, School settings and the rollover too, and drawn once per screen
          rather than twice on this one. It also fills the ~200px of empty cream
          under a six-item nav, which is the same dead space the parent tier's
          sidekick was added for.
        */}
      </div>
    </div>
  )
}

/** A count and its denominator, with an icon in its own colour. */
function Chip({
  tint,
  label,
  value,
  children,
}: {
  tint: string
  label: string
  value: string
  children: ReactNode
}) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-pill border bg-surface/80 py-1.5 pr-3.5 pl-2.5 font-dash text-dash-xs font-bold text-ink-soft"
      style={{ borderColor: `color-mix(in srgb, ${tint} 35%, transparent)` }}
    >
      <span
        aria-hidden="true"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full"
        style={{ background: `color-mix(in srgb, ${tint} 18%, transparent)`, color: tint }}
      >
        {children}
      </span>
      <span className="tabular text-dash-sm font-extrabold text-ink">{value}</span>
      <span className="font-normal">{label}</span>
    </span>
  )
}

/* ────────────────────────────────────────────────────── the five games ── */

/**
 * One game, and how much of the school has met it.
 *
 * 🔴 IT ANSWERS A QUESTION THE TABLE BELOW CANNOT: *which games is this school
 * actually using?* The class table says how many children played **something**;
 * a school where every child plays Sky Quest and nobody has opened Kalqy World
 * looks identical in it. That gap is a curriculum fact — `games.ts` already
 * records that the documented default order omits Kalqy World entirely, so
 * *"a child playing only default sessions never meets Social Awareness"* — and
 * this is the first screen in the product where a school could notice.
 *
 * ⚠️ "0 of 31 children" IS A REAL ANSWER AND IS DRAWN AS ONE. The row is built
 * from the enum by `foldSchoolSkills`, so an untouched game keeps its card and
 * says nobody has played it — a card that vanished would leave a school
 * believing it owns four games.
 */
export function GameUsageCard({
  row,
  childCount,
  lang,
  index,
  onOpen,
}: {
  row: SchoolSkillRow
  /** The school's own child count — the denominator, never re-derived here. */
  childCount: number
  lang: Lang
  index: number
  /**
   * Open the play log for this game.
   *
   * ⚠️ OPTIONAL, AND ABSENT MEANS THIS IS NOT A BUTTON. The operator tier
   * draws this same card to look at somebody else's school and must not be given
   * a door into a school route — the argument `onNavigate` already makes on E1.
   */
  onOpen?: (game: string) => void
}) {
  const t = ADMIN_COPY[lang]

  /*
   * ⚠️ THE SKILL ARRIVES AS A `string` AND IS NARROWED BEFORE IT INDEXES ANY
   * TABLE. `SchoolSkillRow.skill` is typed loosely by the API module; a motor
   * skill or a retired one reaching this component must render nothing rather
   * than throw on a missing key. `isContentSkill` is generated from the schema,
   * so the check cannot drift from the enum it guards.
   */
  const skill = row.skill as Skill
  if (!isContentSkill(skill)) return null
  const game = GAME_FOR_SKILL[skill]
  const untouched = row.children === 0

  /*
   * 🔴 NO `play-card`, NO GLOW, AND THE REASON IS THAT THIS CARD IS NOT A
   * BUTTON. It carries no `onClick`, no `href`, no `role` and no `tabIndex` —
   * it is a figure to read, like the tiles above it. The playful layer gave it
   * the full hover package anyway: a 4px lift, `--shadow-raised` (`0 8px 24px`,
   * three times the resting shadow), a pointer-tracked radial glow built from
   * `--mx`/`--my`, and a 1.05 zoom on the artwork. Four affordances promising a
   * click that has nowhere to go — an admin who chases one learns only that the
   * page moves under the cursor for no reason.
   *
   * ⚠️ THE BASE `.play-card` RULE IS UNTOUCHED, AND MUST STAY THAT WAY. Every
   * tier's `StatCard` and the whole parent tier ride on it; the fix is this card
   * declining to opt in, never the shared rule losing its lift.
   *
   * ⚠️ `play-rise` STAYS. It is an entrance — the staggered mount the section
   * shares with its neighbours — not an answer to the pointer, so nothing about
   * it promises an interaction.
   *
   * 🔴 AND NOW THAT IT DOES OPEN SOMETHING, IT IS A REAL `<button>` — NOT A
   * DIV WITH AN `onClick`. A div cannot be tabbed to, cannot be pressed with
   * Enter or Space, and is announced as nothing; the whole card is the target, so
   * the target has to be an element that behaves like one. What it still does NOT
   * get back is the shadow: `focus-visible` draws a ring, the cursor turns, and
   * the border takes the game's own colour on hover. None of those move the card
   * or paint a light under the pointer.
   */
  const opens = onOpen !== undefined
  const shell =
    'play-rise relative block w-full overflow-hidden rounded-xl border border-line bg-surface text-left shadow-card'

  const body = (
      <div className="relative">
        <div className="relative overflow-hidden">
          {/* ⚠️ 16/9, NOT 3/2. The plate was two thirds of the card's width tall
              and it is decoration above a two-line figure; the shorter frame is
              what lets three of these sit in the width that held two. `object-
              cover` means no art is letterboxed by the change, only cropped. */}
          <img
            src={game.art}
            alt={game.title[lang]}
            className="block aspect-[16/9] w-full object-cover select-none"
            loading="lazy"
            draggable={false}
          />
          {untouched && (
            /* A warm veil, never a greyscale filter — §1: no grey, anywhere, and
               a desaturated plate reads as artwork that failed to load. */
            <span
              aria-hidden="true"
              className="absolute inset-0"
              style={{ background: 'color-mix(in srgb, var(--color-canvas) 70%, transparent)' }}
            />
          )}
        </div>

        <div className="border-t border-line p-3.5">
          <p
            className="flex items-center gap-2 font-dash text-dash-xs font-bold"
            style={{ color: game.tint }}
          >
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: game.tint }}
            />
            {game.title[lang]}
          </p>

          {/* 🔴 THE COUNT AND ITS DENOMINATOR IN ONE STRING, so the number cannot
              be lifted out of the sentence that gives it meaning. */}
          {/* ⚠️ `dash-md`, DOWN ONE STEP FROM `dash-lg`. It is still the largest
              thing on the card by 4px — the title and the caption are both
              `dash-xs` — so the hierarchy the fraction needs survives the card
              getting smaller around it. */}
          <p className="tabular mt-1.5 text-dash-md leading-tight font-extrabold text-ink">
            {row.children} / {childCount}
          </p>
          <p className="mt-0.5 font-dash text-dash-xs text-muted">
            {untouched ? t.gameUntouched : t.gameChildrenPlayed}
          </p>

          {/* ⚠️ THE ONLY THING THE CARD GAINS BY BEING PRESSABLE, and it is a
              word rather than an animation. A control has to say what it does;
              a card that merely changed colour on hover would still leave a
              reader guessing what clicking it might mean. */}
          {opens && (
            <span
              className="mt-2 flex items-center gap-1 font-dash text-dash-xs font-bold"
              style={{ color: game.tint }}
            >
              {t.gameOpenLog}
              <ChevronIcon size={13} />
            </span>
          )}
        </div>
      </div>
  )

  if (!opens) {
    return (
      <div
        className={shell}
        style={{ '--play-tint': game.tint, '--rise-i': index } as CSSProperties}
      >
        {body}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        onOpen(game.id)
      }}
      /* ⚠️ THE ACCESSIBLE NAME SAYS WHERE IT GOES, NOT WHAT IT SHOWS. A screen
         reader reading the card's own text hears "Sky Quest 23 / 33 children
         have played it" and no hint that it is a door. */
      aria-label={`${game.title[lang]} — ${t.gameOpenLog}`}
      className={`${shell} cursor-pointer transition-colors hover:border-[var(--play-tint)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--play-tint)]`}
      style={{ '--play-tint': game.tint, '--rise-i': index } as CSSProperties}
    >
      {body}
    </button>
  )
}

/* ─────────────────────────────────────────────────── E5 — the coverage ring ── */

/**
 * Covered codes out of all of them, as a ring.
 *
 * 🔴 IT IS A COUNT DRAWN, NOT A PERCENTAGE. The arc is a picture of the same
 * fraction printed inside it — "13 / 16" — and that fraction is the only number
 * on the screen. OD-39/OD-72 forbid the ratio, and a ring is the one chart shape
 * that can show a proportion while still stating both of its terms.
 *
 * 🔴 THE ARC IS `pathLength="100"`, SO THE MATHS IS THE FRACTION AND NOTHING
 * ELSE. Without it the dash array has to be computed from 2πr, which is where a
 * ring quietly ends up drawing 97% of a circle and nobody notices. With it the
 * offset IS the uncovered share, in the units a reader is looking at.
 *
 * ⚠️ `aria-hidden`, WITH THE SENTENCE BESIDE IT. A screen reader gets
 * `coverageCount` as prose; announcing an SVG twice is noise, and a ring that
 * only a sighted reader can use would be the chart carrying the fact.
 */
export function CoverageRing({ covered, total }: { covered: number; total: number }) {
  /* A zero denominator is a school with no curriculum table — impossible from
     the server, and a NaN arc if it ever happened. */
  const share = total > 0 ? Math.max(0, Math.min(1, covered / total)) : 0

  return (
    <div className="relative shrink-0" style={{ width: 132, height: 132 }}>
      <svg viewBox="0 0 120 120" width="132" height="132" aria-hidden="true">
        {/* The whole, in the warm neutral every empty track in the product uses. */}
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="var(--color-pill-neutral-bg)"
          strokeWidth="12"
        />
        {/*
          The covered share. Rotated so it starts at twelve o'clock — a ring that
          begins at three reads as an odd slice rather than as progress.
        */}
        <circle
          className="play-ring"
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="var(--color-leaf)"
          strokeWidth="12"
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray="100"
          strokeDashoffset={100 - share * 100}
          transform="rotate(-90 60 60)"
          style={{ '--ring-offset': String(100 - share * 100) } as CSSProperties}
        />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="tabular text-dash-xl leading-none font-extrabold text-ink">
          {covered}
        </span>
        <span className="tabular text-dash-xs font-bold text-muted">/ {total}</span>
      </span>
    </div>
  )
}

/**
 * One competency code, covered or not.
 *
 * 🔴 SHAPE AND WORD, NEVER COLOUR ALONE — §12. A covered code carries a tick and
 * an uncovered one carries a different glyph, and the two groups sit under their
 * own headings, so the state survives both a colour-blind reader and a
 * photocopier.
 *
 * ⚠️ THE UNCOVERED ONES ARE THE POINT OF THE SCREEN. A wall of green ticks tells
 * a head teacher nothing they can act on; three amber codes tell them which
 * games to schedule. They are drawn second and given the warmer colour for that
 * reason, not as a warning — nothing here is anybody's fault.
 */
export function CodePill({ code, covered }: { code: string; covered: boolean }) {
  return (
    <span
      className="play-day inline-flex items-center gap-1.5 rounded-pill border px-3 py-1.5 font-dash text-dash-xs font-bold"
      style={{
        background: covered
          ? 'color-mix(in srgb, var(--color-leaf) 12%, transparent)'
          : 'var(--color-pill-warning-bg)',
        borderColor: covered
          ? 'color-mix(in srgb, var(--color-leaf) 34%, transparent)'
          : 'var(--color-pill-warning-line)',
        color: covered ? 'var(--color-pill-success-fg)' : 'var(--color-pill-warning-fg)',
      }}
    >
      <span aria-hidden="true" className="inline-flex shrink-0">
        {covered ? <CheckIcon size={13} /> : <InfoIcon size={13} />}
      </span>
      {code}
    </span>
  )
}

/**
 * One game's row of the NCF table, as a card.
 *
 * 🔴 IT REPLACED A COLUMN OF DATABASE SLUGS. The table printed `r.game` raw, so
 * the first and boldest column of a report a school might act on read
 * "sky-quest", "letter-lagoon", "turos-world". The product's own name for each
 * game has existed in `gameArt.ts` all along; the report had no way to reach it
 * until `GAME_ART_BY_ID`.
 *
 * ⚠️ BOTH COUNTS KEEP THEIR LABELS. "730 activities · 23 children" is two facts
 * about different things — a game played hard by a few and a game touched once
 * by everyone are opposite situations with the same total.
 */
export function GameCoverageCard({
  row,
  lang,
  index,
}: {
  row: { game: string; category: string; codes: string[]; events: number; children: number }
  lang: Lang
  index: number
}) {
  const t = ADMIN_COPY[lang]
  const pointer = usePointerVars()
  const art = GAME_ART_BY_ID[row.game as GameId] as GameArt | undefined
  const untouched = row.events === 0

  return (
    <div
      ref={pointer.ref}
      onMouseMove={pointer.onMouseMove}
      onMouseLeave={pointer.onMouseLeave}
      className="play-card play-rise relative overflow-hidden rounded-xl border border-line bg-surface shadow-card"
      style={
        { '--play-tint': art?.tint ?? 'var(--color-primary)', '--rise-i': index } as CSSProperties
      }
    >
      <span className="play-glow" aria-hidden="true" />

      <div className="play-layer relative flex gap-4 p-4">
        {art ? (
          /* A thumbnail rather than the full 3:2 plate — this is a report, and
             the picture is here to identify a row, not to sell the game. */
          <img
            src={art.art}
            alt=""
            className="play-art h-20 w-20 shrink-0 rounded-lg object-cover select-none"
            loading="lazy"
            draggable={false}
          />
        ) : null}

        <div className="min-w-0 flex-1">
          <p className="text-dash-md font-extrabold text-ink">
            {/* ⚠️ Falls back to the id rather than to blank. A game added to the
                server before this table knows it should still name itself. */}
            {art ? art.title[lang] : row.game}
          </p>
          <p className="mt-0.5 font-dash text-dash-xs text-muted">{row.category}</p>

          <p className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 font-dash text-dash-xs text-muted">
            <span>
              <span className="tabular font-extrabold text-ink">{row.events}</span>{' '}
              {t.coverageActivity}
            </span>
            <span>
              <span className="tabular font-extrabold text-ink">{row.children}</span>{' '}
              {t.coverageChildren}
            </span>
          </p>

          <p className="mt-2.5 flex flex-wrap gap-1.5">
            {row.codes.map((code) => (
              <span
                key={code}
                className="rounded-pill border border-line bg-canvas px-2 py-0.5 font-dash text-dash-xs font-bold text-ink-soft"
              >
                {code}
              </span>
            ))}
          </p>

          {untouched && (
            /* ⚠️ A FACT, NOT A FAILURE. `summariseCoverage` calls these "where
               the gap actually is" — the one row on this screen a head teacher
               can do something about this term. */
            <p className="mt-2">
              <StatusPill tone="warning" icon={<InfoIcon size={14} />}>
                {t.coverageUntouched}
              </StatusPill>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * A class's initial, in the colour of its age band.
 *
 * ⚠️ IT IS A LETTER, NOT AN AVATAR. A class has no picture and inventing one
 * would be decoration; the initial is what a head teacher's eye already uses to
 * find "Class 2B" in a column of six. The colour is the band, which is the one
 * property of a class that a colour can carry honestly — it decides which games
 * unlock, so two classes of the same band genuinely belong together.
 */
export function ClassBadge({ name, tint }: { name: string; tint: string }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-dash text-dash-xs font-extrabold"
      style={{
        background: `color-mix(in srgb, ${tint} 14%, transparent)`,
        borderColor: `color-mix(in srgb, ${tint} 32%, transparent)`,
        color: tint,
      }}
    >
      {/* The first character the school itself chose — never a parsed grade. */}
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  )
}

/**
 * The fixed colour for an age band.
 *
 * ⚠️ THREE BANDS, THREE TOKENS, CHOSEN ONCE. A band is identity rather than
 * status — no band is better than another — so the palette is fixed per value
 * and never derived from a number, which is `StatCard`'s own rule for the same
 * reason.
 */
export const BAND_TINT: Record<string, string> = {
  BAND_3_4: 'var(--color-sky)',
  BAND_4_5: 'var(--color-grape)',
  BAND_5_6: 'var(--color-teal)',
}

/** ⚠️ Falls back to the brand rather than to nothing — see `navTint`. */
export const bandTint = (band: string): string => BAND_TINT[band] ?? 'var(--color-primary)'
